import { Box, Step, StepLabel, Stepper, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';

import { QueryClient, dehydrate } from '@tanstack/react-query';
import { handleError, is500 } from '@/logic/shared';
import { useGetSettings, useGetVoucherRedemptionSteps } from '@/logic/services';

import { AxiosError } from 'axios';
import Seo from '@/components/seo/seo';
import { SeoPagesEnum } from '@/constants/seo-pages-enum';
import { findSeoInsidePage } from '@/logic/helpers/find-seo-inside-page';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import { useGetPagesSeoMeta } from '@/logic/services/seo';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const VoucherRedemption: NextPage<{
  path: string;
}> = ({ path }) => {
  const { data: seoDataList } = useGetPagesSeoMeta();
  const seoPage = findSeoInsidePage(
    seoDataList?.pageMetaData,
    SeoPagesEnum.vouchersHowTo
  );

  const theme = useTheme();
  const { data: voucherRedemptionSteps, isLoading } = useGetVoucherRedemptionSteps({});

  const intl = useIntl();
  const { data: allCmsSetting } = useGetSettings();

  return (
    <>
      <Seo
        title={
          seoPage?.titleTag ||
          intl.formatMessage({
            id: 'voucher_redemption',
          })
        }
        canonical={seoPage?.canonicalURL}
        pagePath={path}
        description={
          seoPage?.descriptionTag || allCmsSetting?.voucherRedemptionDescription
        }
        noFollow={seoPage?.hasFollow}
        noIndex={seoPage?.hasIndex}
      />
      <Box
        component='section'
        sx={{
          flexGrow: 1,
          px: {
            laptop: pxToRem(128),
            tablet: pxToRem(40),
            mobile: '16px',
          },
          mb: pxToRem(122),
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: { tablet: pxToRem(30), mobile: '18px' },
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='voucher_redemption' />
        </Typography>

        {!isLoading ? (
          voucherRedemptionSteps.voucherRedemptionSteps.length > 0 ? (
            <Box
              sx={{
                maxWidth: '1102px',
                mx: 'auto',
                background: theme.palette.layout.bgSecondary,
                borderRadius: '25px',
                px: { tablet: pxToRem(140), mobile: '16px' },
                pt: { tablet: pxToRem(32), mobile: '16px' },
                pb: { tablet: pxToRem(43), mobile: '24px' },
              }}
            >
              {allCmsSetting?.voucherRedemptionDescription && (
                <Box
                  component='p'
                  sx={{
                    color: theme.palette.grey[70],
                    fontSize: { tablet: pxToRem(24), mobile: '16px' },
                    mb: { tablet: pxToRem(40), mobile: '24px' },
                    whiteSpace: 'pre-line',
                  }}
                >
                  {allCmsSetting.voucherRedemptionDescription}
                </Box>
              )}

              <Stepper
                orientation='vertical'
                sx={{
                  '& .MuiStepConnector-root ': {
                    transform: { tablet: 'scaleY(2.3)', mobile: 'scaleY(5)' },
                    mx: { tablet: pxToRem(20), mobile: '15px' },
                    height: { tablet: pxToRem(47), mobile: '52px' },
                    '& .MuiStepConnector-line': {
                      height: { tablet: pxToRem(47), mobile: '52px' },
                    },
                  },
                  '&  .MuiStepConnector-root:nth-of-type(2)': {
                    background: 'transparent',
                    [theme.breakpoints.down('tablet')]: {
                      transform: `scaleY(5) translateY(20%)`,
                    },
                    [theme.breakpoints.down(425)]: {
                      transform: ` ${
                        voucherRedemptionSteps?.voucherRedemptionSteps[0]?.desc.length <
                        80
                          ? ' scaleY(5) translateY(20%)'
                          : voucherRedemptionSteps?.voucherRedemptionSteps[0]?.desc
                              .length < 100
                          ? 'scaleY(3) translateY( 10%)'
                          : 'scaleY(4.5) translateY( 10%) '
                      } `,
                    },
                    [theme.breakpoints.down(370)]: {
                      transform: ` ${
                        voucherRedemptionSteps?.voucherRedemptionSteps[0]?.desc.length <
                        80
                          ? ' scaleY(5) translateY(20%)'
                          : voucherRedemptionSteps?.voucherRedemptionSteps[0]?.desc
                              .length < 100
                          ? 'scaleY(5) translateY( 10%)'
                          : voucherRedemptionSteps?.voucherRedemptionSteps[0]?.desc
                              .length < 125
                          ? 'scaleY(4.5) translateY( 10%)'
                          : 'scaleY(5.7) translateY( 0%)'
                      } `,
                    },
                  },
                  '&  .MuiStepConnector-root:nth-last-of-type(2)': {
                    [theme.breakpoints.down('tablet')]: {
                      transform: 'scaleY(5) translateY(-20%)',
                    },
                    [theme.breakpoints.down(380)]: {
                      transform: 'scaleY(5) translateY(-20%)',
                    },
                  },

                  '& .MuiStepConnector-line': {
                    borderColor: '#D1AAD7',
                    borderLeftWidth: '3px',
                  },
                  '& .MuiStepLabel-iconContainer': {
                    padding: 0,
                    outline: `7px solid ${theme.palette.layout.backgroundColor}`,
                    borderRadius: '100%',
                  },
                  '& .MuiStep-root ': {
                    position: 'relative',
                    zIndex: 10,
                  },
                  '.MuiStepConnector-line': {
                    animation: `opacityBorder 2000ms ${theme.transitions.easing.easeInOut} both `,
                    animationDelay: `1.2s`,
                    '@keyframes opacityBorder': {
                      '0%': {
                        opacity: 0,
                      },
                      '100%': {
                        opacity: 1,
                      },
                    },
                  },
                }}
              >
                {voucherRedemptionSteps?.voucherRedemptionSteps?.map((step, index) => (
                  <Step
                    key={step.id}
                    sx={{
                      animation: `translateBottom 1500ms ${theme.transitions.easing.easeInOut} both `,
                      animationDelay: `${index / 5}s`,
                      '@keyframes translateBottom': {
                        '0%': {
                          opacity: 0,
                          transform: 'translateY(+2000px)',
                        },
                        '100%': {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                      },
                      [theme.breakpoints.down('tablet')]: {
                        '.MuiStepLabel-label  > div.MuiBox-root': {
                          alignItems: step.desc.length > 80 ? 'stretch' : 'center',
                        },
                        '.MuiStepLabel-root': {
                          alignItems: step.desc.length > 80 ? 'baseline' : 'center',
                        },
                      },
                    }}
                  >
                    <StepLabel
                      sx={{
                        display: 'flex',
                        '& .MuiSvgIcon-root': {
                          width: { tablet: pxToRem(42), mobile: '31px' },
                          height: { tablet: pxToRem(42), mobile: '31px' },
                          color: theme.palette.primary.main,
                          borderRadius: '100%',
                        },
                        '& .MuiStepIcon-text': {
                          fontSize: { tablet: pxToRem(22), mobile: '16px' },
                        },
                      }}
                    >
                      <Box
                        component='article'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <ImageBoxFuture
                          img={step?.icon}
                          title={intl.formatMessage({
                            id: 'voucher.step_icon',
                          })}
                          objectFit='contain'
                          sx={{
                            minWidth: { tablet: pxToRem(94), mobile: '80px' },
                            height: { tablet: pxToRem(94), mobile: '80px' },
                            mx: { tablet: pxToRem(40), mobile: '16px' },
                            overflow: 'hidden',
                          }}
                        />

                        <Box
                          component='p'
                          sx={{
                            fontSize: { tablet: pxToRem(18), mobile: '14px' },
                            color: theme.palette.grey[70],
                            lineHeight: ' 150%',
                          }}
                        >
                          {step.desc}
                        </Box>
                      </Box>
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          ) : (
            <Box
              component='div'
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                pt: { tablet: pxToRem(37), mobile: '26px' },
                mb: { tablet: pxToRem(32), mobile: '24px' },
              }}
            >
              <ImageBoxFuture
                img='/icons/profile/credit-card/credit-cards-empty.svg'
                title={intl.formatMessage({
                  id: 'voucher_redemption.no_steps',
                })}
                objectFit='contain'
                sx={{
                  mb: { tablet: pxToRem(24), mobile: '24px' },
                  maxWidth: '100%',
                  width: { tablet: pxToRem(250), mobile: '200px' },
                  height: { tablet: pxToRem(182), mobile: '145px' },
                }}
              />

              <Typography
                component='p'
                sx={{
                  fontSize: { tablet: pxToRem(20), mobile: '16px' },
                  textAlign: 'center',
                  color: theme.palette.grey[50],
                  maxWidth: { tablet: pxToRem(461), mobile: '461px' },
                  lineHeight: '150%',
                }}
              >
                <FormattedMessage id='voucher_redemption.no_steps' />
              </Typography>
            </Box>
          )
        ) : (
          <MovieLoaderWrapper
            innerBox={{ width: '200px', height: '200px' }}
            outerBox={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '40vh',
            }}
          />
        )}
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const queryClient = new QueryClient();

  try {
    await useGetVoucherRedemptionSteps.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  try {
    await useGetPagesSeoMeta.fetchQuery(queryClient, {
      headers,
    });
  } catch (error: unknown) {
    if (is500(error)) {
      return handleError(error as AxiosError);
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default VoucherRedemption;
